'use client';

import dynamic from 'next/dynamic';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Printer, Download, Loader2, Settings, Mail, Send, Users } from 'lucide-react';
import EmailRecipientsInput from '@/app/client-zone/modules/total-connect/components/EmailRecipientsInput';
import EmailContactPickerModal from '@/components/reports/EmailContactPickerModal';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/lib/authService';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';
import {
  DOCUMENT_TYPE_LABELS,
  getDefaultTemplateName,
  type DocumentType,
  type UnifiedTemplate,
} from '@/lib/reports';

interface ReportTemplateOption {
  id: number;
  name: string;
  document_type: DocumentType;
  is_default: boolean;
  is_system?: boolean;
}

interface UnifiedDocumentPreviewModalProps {
  documentId: number | string;
  documentType: DocumentType;
  documentNumber?: string | null;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  defaultEmailTo?: string | null;
  defaultEmailName?: string | null;
  onEmailSent?: () => void | Promise<void>;
}

function getTemplateOptionLabel(template: ReportTemplateOption): string {
  const documentLabel = DOCUMENT_TYPE_LABELS[template.document_type] || template.document_type;
  const legacyDefaultName = `Default ${documentLabel} Template`;
  const defaultName = getDefaultTemplateName(template.document_type);

  if (template.name === legacyDefaultName || template.name === `${documentLabel} Template` || template.name === defaultName) {
    return defaultName;
  }

  return template.name;
}

interface DownloadUnifiedDocumentPdfOptions {
  documentId: number | string;
  documentType: DocumentType;
  documentNumber?: string | null;
  templateId?: number | null;
}

const UnifiedDesigner = dynamic(
  () => import('@/components/designer/UnifiedDesigner').then((m) => m.UnifiedDesigner),
  {
    ssr: false,
    loading: () => (
      <div className="flex items-center justify-center h-full bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    ),
  },
);

const SUSPEND_OPERATIONS_REALTIME_DISPATCH_KEY = '__taSuspendOperationsRealtimeDispatchCount';
type PreviewWindowWithRealtimeSuspendFlag = Window & typeof globalThis & {
  __taSuspendOperationsRealtimeDispatchCount?: number;
};

async function fetchUnifiedDocumentPreviewHTML({
  documentId,
  documentType,
  templateId,
}: {
  documentId: number | string;
  documentType: DocumentType;
  templateId?: number | null;
}) {
  const token = authService.getTokens()?.access_token;
  const response = await fetch('/api/v1/reports/generate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      action: 'previewHTML',
      documentType,
      params: {
        workItemId: documentId,
      },
      ...(templateId ? { templateId } : {}),
    }),
  });

  const json = await response.json();
  if (!response.ok || !json.success) {
    throw new Error(json.error || 'Failed to load preview');
  }

  return json.data?.html as string;
}

export async function downloadUnifiedDocumentPdf({
  documentId,
  documentType,
  documentNumber,
  templateId,
}: DownloadUnifiedDocumentPdfOptions) {
  const token = authService.getTokens()?.access_token;
  const response = await fetch('/api/v1/reports/generate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      action: 'generatePDF',
      documentType,
      params: {
        workItemId: documentId,
      },
      ...(templateId ? { templateId } : {}),
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'PDF generation failed');
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = window.document.createElement('a');
  const disposition = response.headers.get('Content-Disposition') || '';
  const filenameMatch = disposition.match(/filename="?([^\"]+)"?/);
  const fallbackLabel = (DOCUMENT_TYPE_LABELS[documentType] || documentType).replace(/\s+/g, '-').toLowerCase();
  a.download = filenameMatch?.[1] || `${fallbackLabel}-${documentNumber || documentId}.pdf`;
  a.href = url;
  window.document.body.appendChild(a);
  a.click();
  window.document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function UnifiedDocumentPreviewModal({
  documentId,
  documentType,
  documentNumber,
  isOpen,
  onClose,
  title,
  defaultEmailTo,
  defaultEmailName,
  onEmailSent,
}: UnifiedDocumentPreviewModalProps) {
  const { user, isRoot } = useAuth();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const canManageTemplates = user?.role === 'owner' || isRoot;
  const templatePreferenceKey = useMemo(
    () => `ta_document_report_template:${documentType}`,
    [documentType],
  );

  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState<ReportTemplateOption[]>([]);
  const [catalogTemplates, setCatalogTemplates] = useState<ReportTemplateOption[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [templateDesignerOpen, setTemplateDesignerOpen] = useState(false);
  const [designerTemplateId, setDesignerTemplateId] = useState<number | undefined>(undefined);
  const [openingDesigner, setOpeningDesigner] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState(defaultEmailTo || '');
  const [emailCc, setEmailCc] = useState('');
  const [emailBcc, setEmailBcc] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [emailSuggestions, setEmailSuggestions] = useState<Array<{ email: string; name?: string; source: 'recent' | 'contact' | 'company' | 'crm' | 'personal'; avatar_url?: string; job_title?: string; contact_type?: string; company?: string }>>([]);
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
  const [selectedEmailSuggestionIndex, setSelectedEmailSuggestionIndex] = useState(-1);
  const [activeEmailField, setActiveEmailField] = useState<'to' | 'cc' | 'bcc'>('to');
  const emailToInputRef = useRef<HTMLInputElement>(null);
  const emailCcInputRef = useRef<HTMLInputElement>(null);
  const emailBccInputRef = useRef<HTMLInputElement>(null);
  const emailSuggestionsRef = useRef<HTMLDivElement>(null);
  const templatePreferenceHydratedRef = useRef(false);
  const selectedTemplate = useMemo(
    () => availableTemplates.find((template) => template.id === selectedTemplateId) || null,
    [availableTemplates, selectedTemplateId],
  );

  const readStoredTemplatePreference = useCallback((): number | null | undefined => {
    if (typeof window === 'undefined') return undefined;
    const raw = window.localStorage.getItem(templatePreferenceKey);
    if (!raw) return undefined;
    if (raw === 'default') return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : undefined;
  }, [templatePreferenceKey]);

  const loadTemplates = useCallback(async (preferredTemplateId?: number | null) => {
    if (!isOpen) return;
    templatePreferenceHydratedRef.current = false;
    setLoadingTemplates(true);

    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(`/api/v1/reports/templates?documentType=${documentType}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error('Failed to load templates');

      const json = await response.json();
      if (!json.success) throw new Error(json.error || 'Failed to load templates');

      const data = json.data || {};
      const nextTemplates: ReportTemplateOption[] = Array.isArray(data.templates) ? data.templates : [];
      const nextCatalog: ReportTemplateOption[] = Array.isArray(data.catalog) ? data.catalog : [];
      const storedPreference = readStoredTemplatePreference();

      setAvailableTemplates(nextTemplates);
      setCatalogTemplates(nextCatalog);

      setSelectedTemplateId((current) => {
        const requested = preferredTemplateId !== undefined
          ? preferredTemplateId
          : storedPreference !== undefined
            ? storedPreference
            : current;

        if (requested && nextTemplates.some((template) => template.id === requested)) {
          return requested;
        }

        if (requested === null) {
          return null;
        }

        if (storedPreference && nextTemplates.some((template) => template.id === storedPreference)) {
          return storedPreference;
        }

        return nextTemplates.find((template) => template.is_default)?.id ?? nextTemplates[0]?.id ?? null;
      });
      templatePreferenceHydratedRef.current = true;
    } catch (error) {
      console.error('[UnifiedDocumentPreviewModal] Failed to load templates:', error);
      setAvailableTemplates([]);
      setCatalogTemplates([]);
      setSelectedTemplateId(null);
    } finally {
      setLoadingTemplates(false);
    }
  }, [documentType, isOpen, readStoredTemplatePreference]);

  const loadPreview = useCallback(async (templateIdOverride?: number | null) => {
    if (!isOpen) return;

    setLoading(true);
    try {
      const html = await fetchUnifiedDocumentPreviewHTML({
        documentId,
        documentType,
        templateId: templateIdOverride !== undefined ? templateIdOverride : selectedTemplateId,
      });
      setHtmlContent(html);
    } catch (error: any) {
      console.error('[UnifiedDocumentPreviewModal] Preview load error:', error);
      setHtmlContent(`
        <html>
          <body style="font-family:sans-serif;padding:40px;text-align:center;color:#666">
            <h2>Preview Unavailable</h2>
            <p>${error.message || 'Could not generate preview'}</p>
          </body>
        </html>
      `);
    } finally {
      setLoading(false);
    }
  }, [documentId, documentType, isOpen, selectedTemplateId]);

  const handleDownloadPDF = useCallback(async () => {
    setDownloading(true);
    try {
      await downloadUnifiedDocumentPdf({
        documentId,
        documentType,
        documentNumber,
        templateId: selectedTemplateId,
      });
      toast.success('PDF downloaded');
    } catch (error: any) {
      console.error('[UnifiedDocumentPreviewModal] PDF download error:', error);
      toast.error(error.message || 'Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  }, [documentId, documentNumber, documentType, selectedTemplateId]);

  const handlePrint = useCallback(() => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.print();
    }
  }, []);

  // Email suggestion helpers
  const extractEmailAddresses = useCallback((value: string): string[] => {
    if (!value?.trim()) return [];
    const parts: string[] = [];
    let current = '';
    let inBracket = false;
    for (const char of value) {
      if (char === '<') inBracket = true;
      if (char === '>') inBracket = false;
      if (char === ',' && !inBracket) {
        if (current.trim()) parts.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    if (current.trim()) parts.push(current.trim());
    return parts.map((p) => {
      const m = p.match(/<([^<>]+@[^<>]+)>/);
      return m ? m[1].trim() : p.trim();
    }).filter((e) => e.includes('@'));
  }, []);

  const loadEmailSuggestions = useCallback(async (query: string) => {
    const normalizeEmailSuggestion = (suggestion: any) => ({
      email: suggestion.email,
      name: suggestion.name,
      source: (['recent', 'contact', 'company', 'crm', 'personal'] as const).includes(suggestion.source)
        ? suggestion.source
        : 'recent',
      avatar_url: suggestion.avatar_url,
      job_title: suggestion.job_title,
      contact_type: suggestion.contact_type,
      company: suggestion.company,
    });

    if (!query || query.length < 2) {
      setEmailSuggestions([]);
      setShowEmailSuggestions(false);
      return;
    }
    try {
      const q = query.toLowerCase();
      const recent = JSON.parse(localStorage.getItem('recentEmailRecipients') || '[]');
      const company = JSON.parse(localStorage.getItem('companyEmailUsers') || '[]');
      const cached = [...recent, ...company].filter(
        (s: any) => s.email?.toLowerCase().includes(q) || s.name?.toLowerCase().includes(q),
      );
      const unique = cached.filter(
        (s: any, i: number, arr: any[]) => arr.findIndex((x: any) => x.email?.toLowerCase() === s.email?.toLowerCase()) === i,
      );
      setEmailSuggestions(unique.slice(0, 8).map(normalizeEmailSuggestion));
      setShowEmailSuggestions(unique.length > 0);
      setSelectedEmailSuggestionIndex(-1);

      const token = authService.getTokens()?.access_token;
      if (token) {
        const resp = await fetch('/api/v1/email-contacts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ action: 'searchAllContacts', query }),
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data.success && data.data?.length > 0) {
            const all = [...unique, ...data.data.map((c: any) => ({
              email: c.email, name: c.name, source: c.source, avatar_url: c.avatar_url || c.photo_url,
              job_title: c.job_title, contact_type: c.contact_type, company: c.company,
            }))];
            const deduped = all.filter(
              (s: any, i: number, arr: any[]) => arr.findIndex((x: any) => x.email?.toLowerCase() === s.email?.toLowerCase()) === i,
            );
            setEmailSuggestions(deduped.slice(0, 10).map(normalizeEmailSuggestion));
            setShowEmailSuggestions(deduped.length > 0);
          }
        }
      }
    } catch { /* use cached */ }
  }, []);

  const selectEmailSuggestion = useCallback((suggestion: { email: string; name?: string }) => {
    const setter = activeEmailField === 'cc' ? setEmailCc : activeEmailField === 'bcc' ? setEmailBcc : setEmailTo;
    const current = activeEmailField === 'cc' ? emailCc : activeEmailField === 'bcc' ? emailBcc : emailTo;
    const parts = current.split(',').map((p) => p.trim());
    parts[parts.length - 1] = suggestion.name ? `${suggestion.name} <${suggestion.email}>` : suggestion.email;
    setter(parts.join(', ') + ', ');
    setShowEmailSuggestions(false);
    setEmailSuggestions([]);
    setSelectedEmailSuggestionIndex(-1);
  }, [activeEmailField, emailTo, emailCc, emailBcc]);

  const handleEmailFieldKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showEmailSuggestions || emailSuggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedEmailSuggestionIndex((i) => Math.min(i + 1, emailSuggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedEmailSuggestionIndex((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && selectedEmailSuggestionIndex >= 0) { e.preventDefault(); selectEmailSuggestion(emailSuggestions[selectedEmailSuggestionIndex]); }
    else if (e.key === 'Escape') { setShowEmailSuggestions(false); }
  }, [showEmailSuggestions, emailSuggestions, selectedEmailSuggestionIndex, selectEmailSuggestion]);

  const handleEmailSend = useCallback(async () => {
    const recipients = extractEmailAddresses(emailTo);

    if (recipients.length === 0) {
      toast.error('Please enter at least one recipient email address');
      return;
    }

    const ccRecipients = extractEmailAddresses(emailCc);
    const bccRecipients = extractEmailAddresses(emailBcc);

    setEmailSending(true);
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch('/api/v1/reports/email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          documentType,
          params: {
            workItemId: documentId,
          },
          ...(selectedTemplateId ? { templateId: selectedTemplateId } : {}),
          email: {
            to: recipients,
            ...(ccRecipients.length > 0 ? { cc: ccRecipients } : {}),
            ...(bccRecipients.length > 0 ? { bcc: bccRecipients } : {}),
            ...(emailSubject.trim() ? { subject: emailSubject.trim() } : {}),
            ...(emailMessage.trim() ? { message: emailMessage.trim() } : {}),
          },
        }),
      });

      const json = await response.json().catch(() => ({}));
      if (!response.ok || !json.success) {
        throw new Error(json.error || 'Failed to send email');
      }

      const sentCount = Number(json.data?.queued || json.data?.sent || recipients.length);
      toast.success(`Emailed ${sentCount} recipient${sentCount === 1 ? '' : 's'}`);
      setEmailOpen(false);
      setEmailMessage('');
      setEmailCc('');
      setEmailBcc('');
      setEmailSubject('');
      setShowCcBcc(false);
      if (onEmailSent) {
        await onEmailSent();
      }
    } catch (error: any) {
      console.error('[UnifiedDocumentPreviewModal] Email error:', error);
      toast.error(error.message || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  }, [documentId, documentType, emailMessage, emailTo, emailCc, emailBcc, emailSubject, extractEmailAddresses, onEmailSent, selectedTemplateId]);

  const appendRecipientsToField = useCallback((recipients: Array<{ email: string; name?: string }>, field: 'to' | 'cc' | 'bcc' = 'to') => {
    const currentValue = field === 'cc' ? emailCc : field === 'bcc' ? emailBcc : emailTo;
    const currentEmails = new Set(extractEmailAddresses(currentValue).map((email) => email.toLowerCase()));
    const existingParts = currentValue
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);

    const additions = recipients
      .filter((recipient) => !currentEmails.has(recipient.email.toLowerCase()))
      .map((recipient) => recipient.name ? `${recipient.name} <${recipient.email}>` : recipient.email);

    const nextValue = [...existingParts, ...additions].join(', ');

    if (field === 'cc') {
      setEmailCc(nextValue ? `${nextValue}, ` : '');
    } else if (field === 'bcc') {
      setEmailBcc(nextValue ? `${nextValue}, ` : '');
    } else {
      setEmailTo(nextValue ? `${nextValue}, ` : '');
    }
  }, [emailBcc, emailCc, emailTo, extractEmailAddresses]);

  const openTemplateDesigner = useCallback(async () => {
    if (!canManageTemplates) {
      toast.error('Only owners and root users can customize report templates');
      return;
    }

    setOpeningDesigner(true);
    try {
      let nextTemplateId = selectedTemplateId;

      if (!nextTemplateId && availableTemplates.length > 0) {
        nextTemplateId = availableTemplates.find((template) => template.is_default)?.id ?? availableTemplates[0]?.id ?? null;
      }

      if (!nextTemplateId && catalogTemplates.length > 0) {
        const sourceCatalog = catalogTemplates.find((template) => template.document_type === documentType) ?? catalogTemplates[0];
        const token = authService.getTokens()?.access_token;
        const response = await fetch('/api/v1/reports/templates', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ action: 'enableCatalog', catalogId: sourceCatalog.id }),
        });

        const json = await response.json();
        if (!response.ok || !json.success) {
          throw new Error(json.error || 'Failed to enable template');
        }

        nextTemplateId = json.data?.id ?? null;
        if (nextTemplateId) {
          setSelectedTemplateId(nextTemplateId);
          await loadTemplates(nextTemplateId);
        }
      }

      setDesignerTemplateId(nextTemplateId ?? undefined);
      setTemplateDesignerOpen(true);
    } catch (error: any) {
      console.error('[UnifiedDocumentPreviewModal] Failed to open template designer:', error);
      toast.error(error.message || 'Unable to open template designer');
    } finally {
      setOpeningDesigner(false);
    }
  }, [availableTemplates, canManageTemplates, catalogTemplates, documentType, loadTemplates, selectedTemplateId]);

  useEffect(() => {
    templatePreferenceHydratedRef.current = false;
  }, [templatePreferenceKey]);

  useEffect(() => {
    if (!templatePreferenceHydratedRef.current) return;
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(templatePreferenceKey, selectedTemplateId ? String(selectedTemplateId) : 'default');
  }, [selectedTemplateId, templatePreferenceKey]);

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen, loadTemplates]);

  useEffect(() => {
    if (isOpen) {
      loadPreview();
    }
  }, [isOpen, loadPreview]);

  useEffect(() => {
    if (!isOpen) {
      setEmailOpen(false);
      setContactPickerOpen(false);
      setEmailMessage('');
      setEmailCc('');
      setEmailBcc('');
      setEmailSubject('');
      setShowCcBcc(false);
      setShowEmailSuggestions(false);
      return;
    }

    setEmailTo(defaultEmailTo || '');
    const docLabel = DOCUMENT_TYPE_LABELS[documentType] || documentType;
    setEmailSubject(`${docLabel}${documentNumber ? ` ${documentNumber}` : ''}`);
  }, [defaultEmailTo, documentNumber, documentType, isOpen]);

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') {
      return;
    }

    const windowWithFlag = window as PreviewWindowWithRealtimeSuspendFlag;
    const currentCount = Number(windowWithFlag[SUSPEND_OPERATIONS_REALTIME_DISPATCH_KEY] || 0);
    windowWithFlag[SUSPEND_OPERATIONS_REALTIME_DISPATCH_KEY] = Number.isFinite(currentCount) ? currentCount + 1 : 1;

    return () => {
      const nextCount = Number(windowWithFlag[SUSPEND_OPERATIONS_REALTIME_DISPATCH_KEY] || 1) - 1;
      if (nextCount > 0) {
        windowWithFlag[SUSPEND_OPERATIONS_REALTIME_DISPATCH_KEY] = nextCount;
      } else {
        delete windowWithFlag[SUSPEND_OPERATIONS_REALTIME_DISPATCH_KEY];
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[140] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="px-6 py-4 flex items-center justify-between flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #0d2137 100%)' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Printer className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{title || 'Print Preview'}</h2>
              <p className="text-white/60 text-sm flex items-center gap-2 flex-wrap">
                <span>{documentNumber || DOCUMENT_TYPE_LABELS[documentType] || documentType}</span>
                {loadingTemplates ? (
                  <span className="inline-flex items-center gap-1 text-white/50 text-xs">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Loading templates…
                  </span>
                ) : (
                  <span className="text-white/50 text-xs">
                    Template: {selectedTemplate ? getTemplateOptionLabel(selectedTemplate) : 'Default layout'}
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-white/10 rounded-xl">
              <span className="text-[11px] uppercase tracking-wide text-white/60">Template</span>
              <select
                value={selectedTemplateId ?? ''}
                onChange={(event) => setSelectedTemplateId(event.target.value ? Number(event.target.value) : null)}
                className="bg-transparent text-white text-sm outline-none min-w-[180px]"
                disabled={loadingTemplates}
              >
                <option value="" className="text-gray-900">Default layout</option>
                {availableTemplates.map((template) => (
                  <option key={template.id} value={template.id} className="text-gray-900">
                    {getTemplateOptionLabel(template)}{template.is_default ? ' (Default)' : ''}
                  </option>
                ))}
              </select>
            </div>
            {canManageTemplates && (
              <button
                onClick={openTemplateDesigner}
                disabled={openingDesigner}
                className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center gap-2 transition-colors disabled:opacity-50 text-sm"
                title="Edit the report template used for this preview"
              >
                {openingDesigner ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings className="w-4 h-4" />}
                Customize
              </button>
            )}
            <button
              onClick={handlePrint}
              disabled={loading}
              className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center gap-2 transition-colors disabled:opacity-50 text-sm"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
            <button
              onClick={handleDownloadPDF}
              disabled={downloading || loading}
              className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl flex items-center gap-2 transition-colors disabled:opacity-50 text-sm"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download
            </button>
            <button
              onClick={() => setEmailOpen((current) => !current)}
              className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl flex items-center gap-2 transition-colors text-sm"
            >
              <Mail className="w-4 h-4" />
              Email
            </button>
            <button onClick={onClose} className="p-2.5 hover:bg-white/10 rounded-xl transition-colors">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {emailOpen && (
          <div className="px-4 py-3 sm:px-6 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-blue-600" />
                Email {DOCUMENT_TYPE_LABELS[documentType] || documentType}
              </h4>
              {!showCcBcc && (
                <button
                  type="button"
                  onClick={() => setShowCcBcc(true)}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Cc / Bcc
                </button>
              )}
            </div>

            <div className="relative">
              <div className="flex items-start gap-2">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 pt-2 w-8 shrink-0">To</label>
                <div className="flex-1">
                  <EmailRecipientsInput
                    value={emailTo}
                    onChange={setEmailTo}
                    onFocus={() => setActiveEmailField('to')}
                    suggestions={emailSuggestions}
                    showSuggestions={showEmailSuggestions && activeEmailField === 'to'}
                    selectedSuggestionIndex={selectedEmailSuggestionIndex}
                    onLoadSuggestions={loadEmailSuggestions}
                    onSelectSuggestion={selectEmailSuggestion}
                    onKeyDown={handleEmailFieldKeyDown}
                    inputRef={emailToInputRef}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setContactPickerOpen(true)}
                  className={cn(
                    'shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors',
                    emailTo.trim()
                      ? 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600'
                      : 'bg-blue-600 text-white hover:bg-blue-700',
                  )}
                >
                  <Users className="w-3.5 h-3.5" />
                  {emailTo.trim() ? 'CRM list' : 'Pick from CRM'}
                </button>
              </div>

              {!emailTo.trim() && (
                <p className="mt-1 ml-10 text-[11px] text-blue-600 dark:text-blue-400">
                  No recipient yet? Open the CRM list and add one in a click.
                </p>
              )}

              {showCcBcc && (
                <>
                  <div className="flex items-start gap-2 mt-1.5">
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 pt-2 w-8 shrink-0">Cc</label>
                    <div className="flex-1">
                      <EmailRecipientsInput
                        value={emailCc}
                        onChange={setEmailCc}
                        onFocus={() => setActiveEmailField('cc')}
                        suggestions={emailSuggestions}
                        showSuggestions={showEmailSuggestions && activeEmailField === 'cc'}
                        selectedSuggestionIndex={selectedEmailSuggestionIndex}
                        onLoadSuggestions={loadEmailSuggestions}
                        onSelectSuggestion={selectEmailSuggestion}
                        onKeyDown={handleEmailFieldKeyDown}
                        inputRef={emailCcInputRef}
                      />
                    </div>
                  </div>
                  <div className="flex items-start gap-2 mt-1.5">
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 pt-2 w-8 shrink-0">Bcc</label>
                    <div className="flex-1">
                      <EmailRecipientsInput
                        value={emailBcc}
                        onChange={setEmailBcc}
                        onFocus={() => setActiveEmailField('bcc')}
                        suggestions={emailSuggestions}
                        showSuggestions={showEmailSuggestions && activeEmailField === 'bcc'}
                        selectedSuggestionIndex={selectedEmailSuggestionIndex}
                        onLoadSuggestions={loadEmailSuggestions}
                        onSelectSuggestion={selectEmailSuggestion}
                        onKeyDown={handleEmailFieldKeyDown}
                        inputRef={emailBccInputRef}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-start gap-2">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 pt-2 w-8 shrink-0">Subj</label>
              <input
                type="text"
                placeholder="Subject line"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <textarea
              placeholder="Optional message"
              value={emailMessage}
              onChange={(event) => setEmailMessage(event.target.value)}
              rows={2}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 resize-none outline-none focus:ring-1 focus:ring-blue-500"
            />

            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">
                {extractEmailAddresses(emailTo).length > 0
                  ? `${extractEmailAddresses(emailTo).length} recipient${extractEmailAddresses(emailTo).length === 1 ? '' : 's'}${extractEmailAddresses(emailCc).length > 0 ? ` + ${extractEmailAddresses(emailCc).length} Cc` : ''}${extractEmailAddresses(emailBcc).length > 0 ? ` + ${extractEmailAddresses(emailBcc).length} Bcc` : ''}`
                  : 'No recipients'}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setEmailOpen(false);
                    setEmailMessage('');
                    setEmailCc('');
                    setEmailBcc('');
                    setShowCcBcc(false);
                  }}
                  className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEmailSend}
                  disabled={emailSending || !emailTo.trim()}
                  className="flex items-center gap-1.5 px-4 py-1.5 text-xs rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {emailSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  {emailSending ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-800 p-4 sm:p-8">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
            </div>
          ) : (
            <iframe
              ref={iframeRef}
              srcDoc={htmlContent || ''}
              className="bg-white shadow-xl mx-auto rounded-lg"
              style={{
                width: '210mm',
                minHeight: '297mm',
                border: 'none',
              }}
              title={title || 'Print Preview'}
            />
          )}
        </div>

        <EmailContactPickerModal
          isOpen={contactPickerOpen}
          onClose={() => setContactPickerOpen(false)}
          onAddRecipients={(recipients) => appendRecipientsToField(recipients, 'to')}
          title={`Add ${DOCUMENT_TYPE_LABELS[documentType] || documentType} recipients`}
          defaultTab="crm"
        />

        {templateDesignerOpen && canManageTemplates && (
          <div className="absolute inset-0 z-20 bg-slate-50 dark:bg-slate-950">
            <UnifiedDesigner
              type="report-template"
              documentId={designerTemplateId}
              createOptions={{ subtype: documentType }}
              onClose={() => setTemplateDesignerOpen(false)}
              onSaved={async (doc) => {
                const savedTemplate = doc.payload as UnifiedTemplate;
                const nextId = savedTemplate.id ? Number(savedTemplate.id) : null;
                if (nextId) {
                  setSelectedTemplateId(nextId);
                  setDesignerTemplateId(nextId);
                }
                await loadTemplates(nextId);
                await loadPreview(nextId);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
